import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Input,
  DatePicker,
  message,
  Tooltip,
  Dropdown,
  Menu,
  Checkbox,
  Modal,
  Tag,
  Space,
} from "antd";
import {
  SearchOutlined,
  DeleteOutlined,
  EditOutlined,
  DownOutlined,
  RightOutlined,
  ScanOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import moment from "moment";
import "./Capture.css";
import { URL } from "../../helper/Mainurl";
import InviteCapture from "./InviteCapture";
import DeleteConfirmationModal from "../DeleteConfirmationModal";
import {
  CalendarViewDayOutlined,
  LocationOn,
  ScaleOutlined,
  TimerOffOutlined,
  WatchOffOutlined,
} from "@mui/icons-material";
import EditInviteCapture from "./EditInviteCapture";
import { apiClient } from "../../utils/apiClient";

const { RangePicker } = DatePicker;

const statuses = ["Pending Confirmation", "Confirm"];

const statusColors = {
  "Pending Confirmation": "#FD9400",
  "pending confirmation": "#FD9400",
  Confirm: "#55D8C1",
  confirmed: "#55D8C1", // Added this to match API response
};

// Generate Dummy Emails
// const generateDummyEmails = () => {
//     const subjects = [
//         "Chloe’s Birthday Party",
//         "Rebecca & George Wedding",
//         "Ollies 8th Birthday Party",
//         "Chloe’s Birthday Party",
//         "Payment Received: Invoice Confirmation",
//         "Your Subscription is About to Expire",
//         "Weekly Newsletter: Top Industry Trends",
//         "Action Required: Verify Your Email Address",
//         "Congratulations! You've Won a Gift Card",
//         "Security Alert: Unusual Login Attempt Detected"
//     ];
//     const locations = [
//         "New York, NY",
//         "Los Angeles, CA",
//         "London, UK",
//         "Dubai, UAE",
//     ];

//     return Array.from({ length: 10 }, (_, index) => {
//         const startTime = moment().add(index, "days").add(index + 1, "hours"); // Start time varies
//         const endTime = startTime.clone().add(1, "hour"); // End time is 1 hour after start time

//         return {
//             key: index + 1,
//             subject: subjects[index % subjects.length],
//             date: startTime.format("YYYY-MM-DD"), // Keep date format consistent
//             startTime: startTime.format("hh:mm A"), // Start time
//             endTime: endTime.format("hh:mm A"), // End time, exactly 1 hour after start
//             location: locations[index % locations.length], // Rotate locations
//             due: `Due in ${2 + index} Days, 4 Hours`,
//             read: Math.random() > 0.5,
//             status: statuses[index % statuses.length],
//         };
//     });
// };

const CaptureInvitations = () => {
  const token = localStorage.getItem("authToken");
  const [emails, setEmails] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dateRange, setDateRange] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [modalOpenEdit, setModalOpenEdit] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [totalEmails, setTotalEmails] = useState(0);

  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterDateRange, setFilterDateRange] = useState([]);
  useEffect(() => {
    if (!modalOpen) {
      getData(); // Fetch when modal is closed
    }
  }, [modalOpen]);
  const [selectedTab, setSelectedTab] = useState("all");

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 500); // wait 500 ms after typing stops

    return () => clearTimeout(handler); // cleanup old timer
  }, [searchTerm]);

  useEffect(() => {
    // console.log("Fetching emails with filters...", totalEmails, pageSize, selectedTab);

    getData(currentPage); // Ensure page and pageSize are passed
  }, [
    dateRange,
    debouncedSearch,
    filterDateRange,
    currentPage,
    pageSize,
    selectedTab,
  ]);

  const getData = async (page = 1) => {
    // console.log("Fetching data for page:", page);
    setLoading(true);
    setEmails([]);
    setTotalEmails(0);
    try {
      const params = {};

      if (dateRange[0]) params.start_date = dateRange[0];
      if (dateRange[1]) params.end_date = dateRange[1];
      if (debouncedSearch) params.search = debouncedSearch;
      if (selectedTab && selectedTab !== "all") params.tab = selectedTab;

      const response = await apiClient.get(
        `/v1/invite-capture/list/${page}`,
        params
      );

      let data = response.data;

      if (data?.data?.events) {
        const events = data.data.events;
        const transformedEmails = events.map((inviteCapture, index) => ({
          key: inviteCapture.id ?? `inviteCapture-${index}`,
          subject: inviteCapture.title || "No Subject",
          date: inviteCapture.start_date || "Unknown Date",
          startTime: moment.utc(inviteCapture.start_time).format("HH:mm"),
          endTime: moment.utc(inviteCapture.end_time).format("HH:mm"),
          location: inviteCapture.location || "Unknown Location",
          status: inviteCapture.status || "Pending Confirmation",
          reminder_timestamp: inviteCapture.reminder_timestamp || "null",
          start_time: inviteCapture.start_time,
          end_time: inviteCapture.end_time,
          invite_file_path: inviteCapture.invite_file_path,
          note: inviteCapture.note,
          event_id: inviteCapture.event_id,
        }));

        setEmails(transformedEmails);
        setPageSize(20);
        setCurrentPage(page);
        setTotalEmails(Number(data?.data?.total_count || 0));
      }
    } catch (error) {
      message.error("Error fetching emails");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  const showDeleteModal = (members) => {
    setMemberToDelete(Array.isArray(members) ? members : [members]); // Ensure array
    setIsDeleteModalOpen(true);
  };
  // Handle single selection
  const onSelectChange = (key) => {
    setSelectedRowKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  // Handle Select All logic
  const handleSelectAll = () => {
    if (selectedRowKeys.length === emails.length) {
      setSelectedRowKeys([]); // Deselect all
    } else {
      setSelectedRowKeys(emails.map((email) => email.key)); // Select all
    }
  };

  // Handle Bulk Delete
  const handleBulkDelete = () => {
    setEmails((prevEmails) =>
      prevEmails.filter((email) => !selectedRowKeys.includes(email.key))
    );
    message.success("Invite Capture deleted!");
    setSelectedRowKeys([]);
  };
  const handleArchive = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning("Please select at least one invite to archive.");
      return;
    }
    setLoading(true);
    // setLoading(true);
    try {
      const response = await fetch(`${URL}/v1/invite-capture/archive`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          invite_capture_ids: selectedRowKeys.map(Number), // Convert keys to numbers
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to archive invites");
      }

      message.success("Invites successfully archived!");

      // Remove archived invites from the list
      setEmails((prevEmails) =>
        prevEmails.filter((email) => !selectedRowKeys.includes(email.key))
      );
      setSelectedRowKeys([]); // Clear selection
    } catch (error) {
      console.error("Error archiving invites:", error);
      message.error("Failed to archive invites. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  const handleUnArchive = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning("Please select at least one invite to archive.");
      return;
    }
    // setLoading(true);
    try {
      const response = await fetch(`${URL}/v1/invite-capture/unarchive`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          invite_capture_ids: selectedRowKeys.map(Number), // Convert keys to numbers
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to archive invites");
      }

      message.success("Invites successfully unarchived");

      // Remove archived invites from the list
      setEmails((prevEmails) =>
        prevEmails.filter((email) => !selectedRowKeys.includes(email.key))
      );
      setSelectedRowKeys([]); // Clear selection
    } catch (error) {
      console.error("Error archiving invites:", error);
      message.error("Failed to archive invites. Please try again.");
    } finally {
      // setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!memberToDelete || memberToDelete.length === 0) return;
    try {
      const response = await fetch(`${URL}/v1/invite-capture/delete`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          invite_capture_ids: memberToDelete.map(Number), // Convert IDs to numbers
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          `Failed to delete: ${errorData.message || response.statusText}`
        );
      }

      // ✅ Remove deleted emails from UI
      setEmails((prevEmails) =>
        prevEmails.filter((email) => !memberToDelete.includes(email.key))
      );

      message.success("Events deleted successfully");
      setSelectedRowKeys([]); // Clear selection
    } catch (error) {
      console.error("Error deleting emails:", error);
      message.error("Failed to delete emails. Please try again.");
    } finally {
      setIsDeleteModalOpen(false);
      setMemberToDelete([]); // Reset array
    }
  };
  // Handle Individual Delete
  const handleDelete = (key) => {
    setEmails((prevEmails) => prevEmails.filter((email) => email.key !== key));
    message.success("Email deleted!");
  };

  // Handle Edit

  const handleEdit = async (key) => {
    setLoading(true);
    try {
      const response = await fetch(`${URL}/v1/invite-capture/show/${key}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) throw new Error("Failed to fetch email details");

      const data = await response.json();
      setSelectedRecord(data.data); // Store fetched data
      setModalOpenEdit(true);
      setLoading(false);
    } catch (error) {
      message.error("Error fetching email details");
      console.error(error);
      setLoading(false);
    }
  };
  // Handle Search Filter
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value.toLowerCase());
  };

  // Handle Date Range Filter
  const handleDateChange = (dates) => {
    if (!dates || dates.length === 0) {
      setDateRange([]); // Reset filter if no date is selected
    } else {
      setDateRange([
        dates[0].format("YYYY-MM-DD"),
        dates[1].format("YYYY-MM-DD"),
      ]);
    }
  };
  // Filter Emails based on search & date range
  const filteredEmails =
    (emails &&
      emails.length &&
      emails.filter((email) => {
        const matchesSearch =
          debouncedSearch === "" ||
          email.subject.toLowerCase().includes(debouncedSearch);

        const matchesDateRange =
          dateRange.length === 0 ||
          (email.date >= dateRange[0] && email.date <= dateRange[1]);

        return matchesSearch && matchesDateRange;
      })) ||
    [];

  const columns = [
    {
      title: "",
      key: "select",
      align: "center",
      width: 50,
      onCell: () => ({
        "data-label": "Select",
        className: "capture-select-cell",
      }),
      render: (_, record) => (
        <Checkbox
          checked={selectedRowKeys.includes(record.key)}
          onChange={() => onSelectChange(record.key)}
        />
      ),
    },
    {
      title: "",
      dataIndex: "subject",
      key: "subject",
      onCell: () => ({ "data-label": "Invite" }),
      render: (text, record) => (
        <div className="capture-main-cell">
          <span className="capture-invite-title">{text}</span>
          <div className="capture-meta-lines">
            <div className="capture-meta-line">
              <CalendarOutlined />{" "}
              <span>{moment(record.date).format("DD MMMM YYYY")}</span>
            </div>
            <div className="capture-meta-line">
              <ClockCircleOutlined />{" "}
              <span>
                {record.startTime} - {record.endTime}
              </span>
            </div>
            <div className="capture-meta-line">
              <LocationOn style={{ fontSize: "14px" }} />{" "}
              <span>{record.location}</span>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: " ",
      dataIndex: "status",
      key: "status",
      align: "left",
      onCell: () => ({
        "data-label": "Status",
        className: "capture-status-cell",
      }),
      render: (status) => <Tag className="capture-status-tag">{status}</Tag>,
    },
    // {
    //   title: " ",
    //   dataIndex: "nav",
    //   key: "nav",
    //   align: "left",
    //   render: (status) => <RightOutlined style={{ fontSize: "12px" }} />,
    // },
    {
      title: "",
      key: "actions",
      align: "center",
      width: 100,
      onCell: () => ({
        "data-label": "Actions",
        className: "capture-actions-cell",
      }),
      render: (_, record) => (
        <div className="capture-actions">
          {/* <Button
            className="deleteIcon"
            icon={<ScanOutlined />}
            onClick={() => handleEdit(record.key)}
          >
            Re-Scan{" "}
          </Button> */}
          <Tooltip
            title={record.event_id ? "You Can Edit From Calender" : "Edit"}
            trigger={["hover", "click"]} // IMPORTANT for mobile
          >
            <Button
              disabled={record.event_id ? true : false}
              className="capture-edit-btn"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record.key)}
              style={{
                backgroundColor: "#A855F7",
                color: "#fff",
                border: "none",
              }}
            />
          </Tooltip>

          <Tooltip title="Delete" trigger={["hover", "click"]}>
            <Button
              className="capture-delete-btn"
              icon={<DeleteOutlined />}
              onClick={() => showDeleteModal(record.key)}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  const handleCloseEditModal = () => {
    getData(currentPage);
    setModalOpenEdit(false);
    setSelectedRecord(null); // Reset record when modal closes
  };

  const tabMappings = {
    "All Events": "all",
    "Upcoming Events": "upcoming",
    "Recent Scans": "recent",
    "View Archive": "archive",
  };
  return (
    <>
      <div
        style={{
          padding: "20px",
          backgroundColor: "#fff",
          borderRadius: "8px",
        }}
      >
        <h2>Your Captured Invitations</h2>

        {/* Selection Summary & Bulk Actions */}
        <div className="tab-filter">
          {Object.entries(tabMappings).map(([label, value]) => (
            <Button
              key={value}
              className={`tab-btn ${selectedTab === value ? "active-tab" : ""}`}
              onClick={() => setSelectedTab(value)}
            >
              {label}
            </Button>
          ))}
        </div>
        <div className="emailFilterSelected">
          <div
            className="emailSelected captureSelectedLft"
            style={{
              marginBottom: "16px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              borderRadius: "5px",
              flexWrap: "wrap",
            }}
          >
            <div className="emailSelectAllBtn inviteSelectAllBtn">
              <Checkbox
                onChange={handleSelectAll}
                checked={
                  selectedRowKeys &&
                  selectedRowKeys.length === emails.length &&
                  emails.length > 0
                }
                indeterminate={
                  selectedRowKeys &&
                  selectedRowKeys.length > 0 &&
                  selectedRowKeys.length < emails.length
                }
              />
              <span className="titleSelectAll">Select all</span>
            </div>
            <span>
              {selectedRowKeys && selectedRowKeys.length} of{" "}
              {emails && emails.length} Selected
            </span>
            {selectedRowKeys && selectedRowKeys.length > 0 && (
              <>
                <Space>
                  <Button
                    className="deleteIcon"
                    icon={<DeleteOutlined />}
                    onClick={() => showDeleteModal(selectedRowKeys)} // Pass selected keys array
                  />
                  {selectedTab === "archive" ? (
                    <Button icon={<PlusOutlined />} onClick={handleUnArchive}>
                      Add To Unarchive
                    </Button>
                  ) : (
                    <Button icon={<PlusOutlined />} onClick={handleArchive}>
                      Add To Archive
                    </Button>
                  )}
                </Space>
              </>
            )}
          </div>

          {/* Filters & Search */}
          <div
            className="emailFilter captureSelectedRgt date-and-addNew"
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
              marginBottom: "16px",
              alignItems: "center",
            }}
          >
            {/* <Dropdown overlay={<Menu><Menu.Item key="high">High Priority</Menu.Item></Menu>}>
        <Button>Priority <DownOutlined /></Button>
    </Dropdown>
    <Dropdown overlay={<Menu><Menu.Item key="keyword">Keyword Filter</Menu.Item></Menu>}>
        <Button>Keyword <DownOutlined /></Button>
    </Dropdown> */}
            <RangePicker
              className="capture-date-range"
              dropdownClassName="capture-range-picker-dropdown"
              onChange={handleDateChange}
              value={
                dateRange.length
                  ? [moment(dateRange[0]), moment(dateRange[1])]
                  : []
              }
            />
            <Button
              onClick={() => {
                setModalOpen(true);
                setSearchTerm("");
                setDateRange([]);
              }}
            >
              Add New Invite +
            </Button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="emailSearch">
          <Input
            placeholder="Search..."
            value={searchTerm}
            onChange={handleSearchChange}
            style={{ height: "50px" }}
            prefix={<SearchOutlined style={{ color: "#888" }} />}
            className="emailSearchInput"
          />
        </div>

        {/* Email Table */}
        <div className="capture-table-wrapper" style={{ marginTop: "10px" }}>
          <Table
            className="hide-table-header capture-table"
            dataSource={filteredEmails}
            columns={columns}
            loading={loading}
            pagination={filteredEmails.length > 10 ? { pageSize: 10 } : false}
            rowKey="key"
            rowClassName={(record) =>
              `emailRows ${
                selectedRowKeys.includes(record.key) ? "selected-row" : ""
              }`
            }
            onRow={(record) => ({
              onDoubleClick: () => handleEdit(record.key), // 👈 open on double-click
            })}
            scroll={{ x: true }}
          />
        </div>
      </div>
      <div>
        {modalOpen && (
          <InviteCapture
            showModal={modalOpen}
            closeModal={() => setModalOpen(false)}
            getData={getData}
            setSearchTerm={setSearchTerm}
            setDateRange={setDateRange}
            setSelectedTab={setSelectedTab}
          />
        )}
        {modalOpenEdit && selectedRecord && (
          <EditInviteCapture
            showModal={modalOpenEdit}
            closeModal={handleCloseEditModal} // Pass the updated function
            record={selectedRecord} // Pass record if needed
          />
        )}
      </div>
      {isDeleteModalOpen && (
        <DeleteConfirmationModal
          visible={isDeleteModalOpen}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setIsDeleteModalOpen(false)}
        />
      )}
    </>
  );
};

export default CaptureInvitations;
